/* =============================================================================
   DOM-BINDINGS — zentrale Event-Verkabelung statt Inline-Handler
   =============================================================================
   Session 23 (M-01 + M-02): Strikte Content-Security-Policy (`script-src
   'self'` ohne `'unsafe-inline'`) verbietet Inline-Event-Handler im HTML
   (`onclick="..."`, `oninput="..."`, etc.). Über das Projekt verteilt liegen
   ~305 solcher Inline-Handler in dynamisch generierten HTML-Strings, die
   per `innerHTML` in den DOM kommen.

   Dieses Modul löst das Problem ohne 305 manuelle Touch-Points:

   1. **MutationObserver** auf `document.body` fängt jeden neu hinzugefügten
      DOM-Knoten ab und konvertiert seine Inline-Handler in echte
      `addEventListener`-Bindings.

   2. **Mini-Parser** versteht das Vokabular, das in den View-Modulen
      tatsächlich vorkommt — keine vollständige JS-Engine, sondern genau
      die Patterns, die das Projekt nutzt:
        - `funcName(arg1, arg2, ...)`
        - `event.stopPropagation()`, `event.preventDefault()`
        - `closeModal()`, `closeCancelView()`, `return false;`
        - String-/Number-Literale, `this.value`, `this.checked`, `event`
        - Mehrere Statements per `;`-Separator
        - Variable-Assignment auf `window.*` (z. B. `resFilter.status=this.value`)
      Komplexere Inline-Ausdrücke (`Math.max(MIN, Math.min(MAX, parseInt(...)))`)
      wurden in Wrapper-Helpers (siehe `core/setting-helpers.js`) ausgelagert.

   3. **Idempotent**: bereits konvertierte Knoten werden via Flag markiert.
============================================================================ */

/* ---------- Helper-Utilities -------------------------------------------- */

function safeCall(fn, ...args) {
  if (typeof fn === 'function') {
    try {
      return fn(...args);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[dom-bindings] Handler-Fehler:', err);
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn('[dom-bindings] Handler nicht verfügbar:', fn);
  }
}

/* ---------- Mini-Expression-Parser -------------------------------------- */

/**
 * Teilt einen Inline-Handler-String in Statements auf, getrennt durch `;`.
 * Respektiert dabei String-Literale ('...', "...") und ineinander geschachtelte
 * Klammern, sodass z. B. ein Komma in einer Funktions-Args-Liste nicht als
 * Statement-Trenner gilt.
 */
function splitStatements(input) {
  const out = [];
  let depth = 0;
  let quote = null;
  let buf = '';
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote) {
      buf += ch;
      if (ch === '\\' && i + 1 < input.length) {
        buf += input[++i];
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'" || ch === '`') {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') depth++;
    else if (ch === ')' || ch === ']' || ch === '}') depth--;
    if (ch === ';' && depth === 0) {
      if (buf.trim()) out.push(buf.trim());
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

/**
 * Teilt eine Args-Liste auf — Komma-Separator, respektiert Strings + Klammern.
 */
function splitArgs(input) {
  const out = [];
  let depth = 0;
  let quote = null;
  let buf = '';
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote) {
      buf += ch;
      if (ch === '\\' && i + 1 < input.length) {
        buf += input[++i];
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'" || ch === '`') {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') depth++;
    else if (ch === ')' || ch === ']' || ch === '}') depth--;
    if (ch === ',' && depth === 0) {
      out.push(buf.trim());
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

const SENTINEL = { type: 'sentinel' };
const SENT_THIS_VALUE = { __sentinel: 'this.value' };
const SENT_THIS_CHECKED = { __sentinel: 'this.checked' };
const SENT_EVENT = { __sentinel: 'event' };

/**
 * Wertet ein einzelnes Argument aus. Erkennt:
 *   - String-Literal: 'foo' / "foo" / `foo`
 *   - Number-Literal: 42, 3.14, -5
 *   - Boolean / null / undefined
 *   - this.value / this.checked / event → Sentinels (zur Listener-Zeit ersetzt)
 *   - parseInt(this.value, 10) / Number(this.value) → number-cast Sentinel
 *   - Verschachtelte simple calls (z. B. `htmlEscape(x)`) — werden best-effort
 *     als String genommen (wenn Klammern korrekt sind).
 */
function evalArg(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  // String-Literale
  const m1 = trimmed.match(/^(['"`])(.*)\1$/s);
  if (m1) {
    // Backslash-Entfärbung minimal (\\' → ')
    return m1[2].replace(/\\(['"\\`])/g, '$1');
  }

  // Number
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (trimmed === 'undefined') return undefined;

  // this.* / event-Sentinels
  if (trimmed === 'this.value') return SENT_THIS_VALUE;
  if (trimmed === 'this.checked') return SENT_THIS_CHECKED;
  if (trimmed === 'event') return SENT_EVENT;
  if (trimmed === 'this') return { __sentinel: 'this' };

  // parseInt(this.value, 10) / parseInt(this.value)
  const piMatch = trimmed.match(/^parseInt\s*\(\s*this\.value\s*(?:,\s*\d+\s*)?\)\s*(?:\|\|\s*(\d+))?$/);
  if (piMatch) {
    return { __sentinel: 'parseInt(this.value)', fallback: piMatch[1] ? Number(piMatch[1]) : 0 };
  }
  // parseFloat(this.value)
  if (/^parseFloat\s*\(\s*this\.value\s*\)$/.test(trimmed)) {
    return { __sentinel: 'parseFloat(this.value)' };
  }
  // Number(this.value)
  if (/^Number\s*\(\s*this\.value\s*\)$/.test(trimmed)) {
    return { __sentinel: 'Number(this.value)' };
  }

  // Verschachtelter Funktions-Call: `fnName(args)` — z. B. `todayISO()`,
  // `nowSnappedToHalfHour()`. Wird zur Listener-Zeit aufgerufen.
  const nestedCall = trimmed.match(/^([a-zA-Z_$][\w$]*)\s*\((.*)\)\s*$/s);
  if (nestedCall) {
    return {
      __sentinel: 'call',
      fn: nestedCall[1],
      args: splitArgs(nestedCall[2]).map(evalArg),
    };
  }

  // Identifier ohne Klammern → window-Property lesen (z. B. `occupancyDate`).
  if (/^[a-zA-Z_$][\w$]*$/.test(trimmed)) {
    return { __sentinel: 'ident', name: trimmed };
  }

  // Member-Pfad ohne Klammern → window.a.b.c (z. B. `state.foo`).
  if (/^[a-zA-Z_$][\w$]*(?:\.[a-zA-Z_$][\w$]*)+$/.test(trimmed)) {
    return { __sentinel: 'path', path: trimmed };
  }

  // Komplexere Ausdrücke: nicht parsen, sondern als Sentinel "raw expression"
  // markieren — der Listener loggt dann eine Warnung.
  return { __sentinel: 'raw', raw: trimmed };
}

/**
 * Wandelt Sentinels zur Listener-Zeit in echte Werte um.
 */
function resolveArg(arg, event) {
  if (arg && typeof arg === 'object' && arg.__sentinel) {
    const target = event && (event.currentTarget || event.target);
    switch (arg.__sentinel) {
      case 'this.value':
        return target ? target.value : undefined;
      case 'this.checked':
        return target ? target.checked : undefined;
      case 'this':
        return target;
      case 'event':
        return event;
      case 'parseInt(this.value)': {
        const v = target ? parseInt(target.value, 10) : NaN;
        return Number.isFinite(v) ? v : arg.fallback;
      }
      case 'parseFloat(this.value)': {
        const v = target ? parseFloat(target.value) : NaN;
        return Number.isFinite(v) ? v : 0;
      }
      case 'Number(this.value)': {
        const v = target ? Number(target.value) : NaN;
        return Number.isFinite(v) ? v : 0;
      }
      case 'call': {
        const fn = window[arg.fn];
        if (typeof fn !== 'function') {
          // eslint-disable-next-line no-console
          console.warn('[dom-bindings] Nested call: Funktion nicht verfügbar:', arg.fn);
          return undefined;
        }
        const resolvedArgs = arg.args.map(a => resolveArg(a, event));
        try { return fn(...resolvedArgs); } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[dom-bindings] Nested call-Fehler:', arg.fn, err);
          return undefined;
        }
      }
      case 'ident':
        return window[arg.name];
      case 'path': {
        const parts = arg.path.split('.');
        let cur = window;
        for (const p of parts) {
          if (cur == null) return undefined;
          cur = cur[p];
        }
        return cur;
      }
      case 'raw':
        // eslint-disable-next-line no-console
        console.warn('[dom-bindings] Komplexer Inline-Ausdruck konnte nicht geparsed werden:', arg.raw);
        return undefined;
      default:
        return undefined;
    }
  }
  return arg;
}

/**
 * Parsed einen Statement-String wie `fn('a','b')` oder `obj.prop=this.value`
 * in eine ausführbare Action-Description.
 */
function parseStatement(stmt) {
  // event.stopPropagation() / event.preventDefault()
  if (/^event\.stopPropagation\s*\(\s*\)$/.test(stmt)) return { kind: 'stop' };
  if (/^event\.preventDefault\s*\(\s*\)$/.test(stmt)) return { kind: 'prevent' };
  if (/^return\s+false$/.test(stmt)) return { kind: 'returnFalse' };

  // Assignment: identifier(.member)*=expression
  const assignMatch = stmt.match(/^([a-zA-Z_$][\w$]*(?:\.[a-zA-Z_$][\w$]*)*)\s*=\s*(.+)$/);
  if (assignMatch && !assignMatch[2].startsWith('=')) {
    return { kind: 'assign', path: assignMatch[1], value: evalArg(assignMatch[2]) };
  }

  // S259 §4 — Methode auf dem Element: `this.select()`. Dieselbe Wurzel wie die
  // `this.x = …`-Zuweisung: `this` ist das Element, nicht window. Bisher fiel
  // das auf `unknown` und stand als bekannte Lücke in der Test-Ausnahmeliste
  // (fünf `onfocus="this.select()"` in den Stammdaten-Editoren — Inhalt bei
  // Fokus markieren, kosmetisch, aber es tat nie etwas).
  const thisCallMatch = stmt.match(/^this\.([a-zA-Z_$][\w$]*)\s*\((.*)\)\s*$/s);
  if (thisCallMatch) {
    return { kind: 'thisCall', method: thisCallMatch[1], args: splitArgs(thisCallMatch[2]).map(evalArg) };
  }

  // Function call: fnName(args)
  const callMatch = stmt.match(/^([a-zA-Z_$][\w$]*)\s*\((.*)\)\s*$/s);
  if (callMatch) {
    const fnName = callMatch[1];
    const argsList = splitArgs(callMatch[2]).map(evalArg);
    return { kind: 'call', fn: fnName, args: argsList };
  }

  // `if(event.target===this){closeTakeawayModal();}` — manuell als Sonderfall
  // (kommt genau einmal vor in takeaway-form.js); nicht generisch parsen.
  const ifBackdropMatch = stmt.match(/^if\s*\(\s*event\.target\s*===\s*this\s*\)\s*\{\s*([a-zA-Z_$][\w$]*)\s*\(\s*\)\s*;?\s*\}\s*$/);
  if (ifBackdropMatch) {
    return { kind: 'ifBackdrop', fn: ifBackdropMatch[1] };
  }

  return { kind: 'unknown', raw: stmt };
}

/* ---------- Inline-Handler-Konverter ------------------------------------ */

const INLINE_ATTRS = [
  'onclick', 'onchange', 'oninput', 'onsubmit',
  'onkeydown', 'onkeyup', 'onkeypress',
  'onfocus', 'onblur',
  'onmouseover', 'onmouseout',
  'onmousedown', 'onmouseup', 'onmousemove',
  'ondblclick',
  'ontouchstart', 'ontouchmove', 'ontouchend',
  'onload', 'onerror',
];

/**
 * Events, bei denen `preventDefault()` aus dem Listener üblich/notwendig ist
 * (z. B. Drag-Start, der das Scrolling unterbinden muss). Chrome warnt, wenn
 * solche Listener nicht explizit als `{passive: false}` registriert sind —
 * indem wir das hier setzen, machen wir dem Browser klar: "absichtlich
 * nicht-passiv", und die Console-Spam-Warnung verschwindet.
 */
const NON_PASSIVE_EVENTS = new Set([
  'touchstart', 'touchmove', 'wheel', 'mousewheel',
]);

function makeListener(stmts) {
  return function listener(event) {
    let blockDefault = false;
    for (const st of stmts) {
      switch (st.kind) {
        case 'stop':
          if (event && event.stopPropagation) event.stopPropagation();
          break;
        case 'prevent':
          if (event && event.preventDefault) event.preventDefault();
          break;
        case 'returnFalse':
          if (event) {
            if (event.preventDefault) event.preventDefault();
            if (event.stopPropagation) event.stopPropagation();
          }
          blockDefault = true;
          break;
        case 'ifBackdrop': {
          if (event && event.target === event.currentTarget) {
            const fn = window[st.fn];
            safeCall(fn);
          }
          break;
        }
        case 'call': {
          const fn = window[st.fn];
          const resolved = st.args.map(a => resolveArg(a, event));
          safeCall(fn, ...resolved);
          break;
        }
        case 'thisCall': {
          // S259 §4 — `this.select()` & Co. auf dem Element, das den Handler trug.
          const el = event && event.currentTarget;
          const fn = el && el[st.method];
          if (typeof fn !== 'function') break;
          const resolved = st.args.map(a => resolveArg(a, event));
          try { fn.apply(el, resolved); }
          catch (err) { console.error('[dom-bindings] this.' + st.method + '():', err); }
          break;
        }
        case 'assign': {
          // Pfad-basiertes Assignment auf window: a.b.c = value
          const parts = st.path.split('.');
          // S259 §4 — `this.x = …` zielt auf das ELEMENT, nicht auf window.
          // Vorher lief auch dieser Fall gegen `window`: `window['this']` ist
          // undefined, und der Zweig unten steigt dann mit `return` aus — was
          // nicht nur die Zuweisung verwirft, sondern den GANZEN Listener ab
          // dem ersten Statement. Genau daran hingen die neun Logo-Rückfälle
          // (`this.onerror=null; this.src='…'`): parsebar, registriert, und
          // trotzdem wirkungslos. `event.currentTarget` ist das Element, an dem
          // der Handler hing — dasselbe, was `this` im nativen Inline-Handler
          // bedeutet hätte.
          const onThis = parts[0] === 'this';
          let target = onThis ? (event && event.currentTarget) : window;
          if (!target) return;
          for (let i = onThis ? 1 : 0; i < parts.length - 1; i++) {
            if (target[parts[i]] == null) {
              // Wenn der Zwischenpfad nicht existiert, ist es entweder eine
              // typo oder ein noch-nicht-initialisiertes Modul-Global.
              return;
            }
            target = target[parts[i]];
          }
          target[parts[parts.length - 1]] = resolveArg(st.value, event);
          break;
        }
        case 'unknown':
          // eslint-disable-next-line no-console
          console.warn('[dom-bindings] Unbekanntes Statement:', st.raw);
          break;
      }
    }
    return blockDefault ? false : undefined;
  };
}

function convertElement(el) {
  if (!el || el.nodeType !== 1) return;
  if (el.__inlineHandlersConverted) return;

  for (const attr of INLINE_ATTRS) {
    const value = el.getAttribute(attr);
    if (!value) continue;

    const stmts = splitStatements(value).map(parseStatement);
    const eventName = attr.slice(2); // "onclick" → "click"
    const listener = makeListener(stmts);

    // Bei touch*/wheel-Events explizit `{passive: false}` setzen — sonst spammt
    // Chrome bei jedem Re-Render eine "non-passive listener"-Violation in die
    // Console. Die Logik dieser Listener nutzt regelmäßig `preventDefault()`
    // (z. B. Drag-Start im Tischplan), also passive=false ist semantisch korrekt.
    if (NON_PASSIVE_EVENTS.has(eventName)) {
      el.addEventListener(eventName, listener, { passive: false });
    } else {
      el.addEventListener(eventName, listener);
    }

    // Inline-Attribut entfernen (CSP würde es eh ignorieren, aber sauberer).
    el.removeAttribute(attr);

    // S259 §4 — Wettlauf beim Bild-Rückfall schließen. Ein `onerror` am `<img>`
    // ist der einzige Handler, der feuern kann, BEVOR wir ihn registriert
    // haben: das Laden startet, sobald das Markup im DOM steht, und
    // `convertSubtree` läuft erst danach. Ist das Bild zu diesem Zeitpunkt
    // schon fertig (`complete`) und trotzdem ohne Maße (`naturalWidth === 0`),
    // ist es gescheitert — dann holen wir das Ereignis genau einmal nach.
    // Ohne das bliebe der Rückfall in schnellen Fällen (404 aus dem Cache) aus.
    if (eventName === 'error' && el.tagName === 'IMG'
        && el.getAttribute('src') && el.complete && el.naturalWidth === 0) {
      try { listener({ type: 'error', currentTarget: el, target: el }); }
      catch (err) { console.warn('[dom-bindings] Nachgeholter Bild-Fehler:', err); }
    }
  }

  el.__inlineHandlersConverted = true;
}

function convertSubtree(root) {
  if (!root) return;
  if (root.nodeType === 1) {
    convertElement(root);
    // Descendants — qSAll mit den Attr-Selektoren.
    const sel = INLINE_ATTRS.map(a => `[${a}]`).join(',');
    const descendants = root.querySelectorAll ? root.querySelectorAll(sel) : [];
    for (const d of descendants) convertElement(d);
  }
}

/* ---------- Direkt-Bindings für statische index.html-Elemente ----------- */

function installStaticBindings() {
  // Reservierungs-Formular
  const reservationForm = document.getElementById('reservation-form');
  if (reservationForm && !reservationForm.__inlineHandlersConverted) {
    reservationForm.addEventListener('submit', (event) => {
      safeCall(window.submitReservation, event);
    });
    reservationForm.__inlineHandlersConverted = true;
  }

  // Admin-Login-Formular
  const adminLoginForm = document.getElementById('admin-login-form');
  if (adminLoginForm && !adminLoginForm.__inlineHandlersConverted) {
    adminLoginForm.addEventListener('submit', (event) => {
      safeCall(window.doLogin, event);
    });
    adminLoginForm.__inlineHandlersConverted = true;
  }

  // Preorder-Toggle
  const preorderToggle = document.getElementById('r-preorder-toggle');
  if (preorderToggle && !preorderToggle.__inlineHandlersConverted) {
    preorderToggle.addEventListener('change', () => {
      safeCall(window.togglePreorder);
    });
    preorderToggle.__inlineHandlersConverted = true;
  }

  // Modal-Overlay → Backdrop-Klick
  const modalOverlay = document.getElementById('modal-overlay');
  if (modalOverlay && !modalOverlay.__inlineHandlersConverted) {
    modalOverlay.addEventListener('click', (event) => {
      safeCall(window.closeModalOnBackdrop, event);
    });
    modalOverlay.__inlineHandlersConverted = true;
  }

  // Event-Delegation für die index.html-data-action-Buttons (View-Switch,
  // Tab-Switch, Service-Type, Reset, Logout). Wir registrieren das nur
  // einmal — danach kommen alle Klicks hier durch.
  if (!document.body.__staticDelegationInstalled) {
    document.body.addEventListener('click', (event) => {
      const target = event.target.closest('[data-action]');
      if (!target) return;
      const action = target.dataset.action;
      switch (action) {
        case 'view-switch':
          safeCall(window.switchView, target.dataset.view);
          break;
        case 'tab-switch':
          safeCall(window.switchTab, target.dataset.tab);
          break;
        case 'service-type':
          safeCall(window.setServiceType, target.dataset.serviceType);
          break;
        case 'reset-customer-form':
          safeCall(window.resetCustomerForm);
          break;
        case 'logout':
          // S259 §7 — auch hier über die Rückfrage; ein Konto-Logout ist
          // teuer (neuer Magic-Link) und darf kein Versehen sein.
          safeCall(window.confirmAccountLogout || window.doLogout);
          break;
        default:
          break;
      }
    });
    document.body.__staticDelegationInstalled = true;
  }
}

/* ---------- Public API -------------------------------------------------- */

/* Test-Hooks (Phase 27, S25): die Pure-Function-Parser-API wird zusätzlich
   als named export verfügbar gemacht, damit Vitest sie direkt testen kann.
   Laufzeitverhalten bleibt unverändert — keine internen Aufrufstellen ändern
   sich, weil sie weiterhin lokale Referenzen benutzen. Memory-Lesson
   `feedback_gg_hard_way`: lieber additive Exports statt Test-Tricks (eval,
   reflection o.ä.). */
export { splitStatements, splitArgs, evalArg, parseStatement };

/**
 * S259 §4 — Test-Hook: EIN Element umbauen, wie es der MutationObserver täte.
 * Additiv, zur Laufzeit ein No-Op. Ohne den Hook liesse sich nur der PARSER
 * prüfen — und der war beim Logo-Rückfall ja in Ordnung; falsch war erst, was
 * `makeListener` daraus machte.
 */
export { convertElement as _convertElementForTests };

let observer = null;

export function installDomBindings() {
  if (window.__domBindingsInstalled) return;
  window.__domBindingsInstalled = true;

  // Statische Bindings (in index.html dauerhaft vorhanden)
  installStaticBindings();

  // Initial-Sweep über das gesamte Dokument — auch View-Module, die schon
  // gerendert haben, kriegen ihre Inline-Handler aktiviert.
  convertSubtree(document.body);

  // MutationObserver für alle künftigen Inserts (innerHTML, appendChild, …).
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          convertSubtree(node);
        }
      } else if (m.type === 'attributes') {
        // Falls jemand ein Inline-Handler-Attribut neu setzt → auch konvertieren.
        const el = m.target;
        if (INLINE_ATTRS.includes(m.attributeName)) {
          el.__inlineHandlersConverted = false;
          convertElement(el);
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: INLINE_ATTRS,
  });
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installDomBindings, { once: true });
  } else {
    installDomBindings();
  }
  window.installDomBindings = installDomBindings;
}
